"use client";

import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { formatPriceRangeLabel } from "./filter-utils";
import {
	translateAvailability,
	translatePeptideCategory,
	translateResearchLevel,
} from "@/lib/peptide-translations";
import { Check, ChevronDown, Minus, Plus, Search, SlidersHorizontal, X } from "lucide-react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import { Sheet, SheetCloseButton, SheetContent, SheetHeader, SheetTitle } from "@/ui/components/ui/sheet";
import { cn } from "@/lib/utils";

export type SortOption =
	| "featured"
	| "newest"
	| "price_asc"
	| "price_desc"
	| "bestselling"
	| "name_asc"
	| "name_desc"
	| "score_desc";

export interface FilterOption {
	name: string;
	count: number;
	hex?: string;
}

interface CategoryFilterOption {
	id: string;
	name: string;
	slug: string;
	count: number;
}

export interface ActiveFilter {
	key: string;
	label: string;
	value: string;
}

interface FilterBarProps {
	children: ReactNode;
	/** Total count of the FULLY filtered result set (never a single page length). */
	resultCount: number;
	/** 1-based display range of the current page — shown as "start–end of total". */
	resultRange?: { start: number; end: number } | null;
	sortValue: SortOption;
	onSortChange: (value: SortOption) => void;
	activeFilters?: readonly ActiveFilter[];
	onRemoveFilter?: (key: string, value: string) => void;
	onClearFilters?: () => void;
	categoryOptions?: readonly CategoryFilterOption[];
	colorOptions?: readonly FilterOption[];
	sizeOptions?: readonly FilterOption[];
	priceRanges?: readonly { label: string; value: string; count: number }[];
	peptideCategoryOptions?: readonly { name: string; count: number }[];
	researchLevelOptions?: readonly string[];
	availabilityOptions?: readonly string[];
	selectedCategories?: readonly string[];
	selectedColors?: readonly string[];
	selectedSizes?: readonly string[];
	selectedPriceRange?: string | null;
	searchQuery?: string;
	selectedPeptideCategories?: readonly string[];
	selectedResearchLevel?: string;
	selectedAvailability?: string;
	onCategoryToggle?: (slug: string) => void;
	onColorToggle?: (color: string) => void;
	onSizeToggle?: (size: string) => void;
	onPriceRangeChange?: (range: string | null) => void;
	onSearchChange?: (query: string) => void;
	onPeptideCategoryToggle?: (name: string) => void;
	onResearchLevelChange?: (level: string) => void;
	onAvailabilityChange?: (availability: string) => void;
}

const ALL_SECTIONS = [
	"category",
	"price",
	"researchLevel",
	"availability",
	"peptideCategory",
	"color",
	"size",
];

export function FilterBar({
	children,
	resultCount,
	resultRange = null,
	sortValue,
	onSortChange,
	activeFilters = [],
	onRemoveFilter,
	onClearFilters,
	categoryOptions = [],
	colorOptions = [],
	sizeOptions = [],
	priceRanges = [],
	peptideCategoryOptions = [],
	researchLevelOptions = [],
	availabilityOptions = [],
	selectedCategories = [],
	selectedColors = [],
	selectedSizes = [],
	selectedPriceRange = null,
	searchQuery = "",
	selectedPeptideCategories = [],
	selectedResearchLevel = "all",
	selectedAvailability = "all",
	onCategoryToggle,
	onColorToggle,
	onSizeToggle,
	onPriceRangeChange,
	onSearchChange,
	onPeptideCategoryToggle,
	onResearchLevelChange,
	onAvailabilityChange,
}: FilterBarProps) {
	const locale = useLocale();
	const t = useTranslations("plp");
	const { channel = "eu" } = useParams<{ channel: string }>();
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(ALL_SECTIONS));
	// Legacy prescription metadata is not a substantiated catalog availability
	// option. Keep existing URL selections removable through their active pill.
	const visibleAvailabilityOptions =
		locale === "fr"
			? availabilityOptions.filter((value) => value !== "Prescription Available")
			: availabilityOptions;

	const toggleSection = (key: string) => {
		setExpandedSections((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	};

	const hasFilters =
		categoryOptions.length > 0 ||
		colorOptions.length > 0 ||
		sizeOptions.length > 0 ||
		priceRanges.length > 0 ||
		peptideCategoryOptions.length > 0 ||
		researchLevelOptions.length > 0 ||
		visibleAvailabilityOptions.length > 0 ||
		!!onSearchChange;

	const activeFilterCount =
		selectedCategories.length +
		selectedColors.length +
		selectedSizes.length +
		(selectedPriceRange ? 1 : 0) +
		(searchQuery.trim() ? 1 : 0) +
		selectedPeptideCategories.length +
		(selectedResearchLevel !== "all" ? 1 : 0) +
		(selectedAvailability !== "all" ? 1 : 0);
	const resultText =
		resultRange && resultRange.start > 0 && resultCount > resultRange.end - resultRange.start + 1
			? t("resultRange", { start: resultRange.start, end: resultRange.end, total: resultCount })
			: t("resultCount", { count: resultCount });

	/* ─── Shared filter sections (desktop sidebar + mobile sheet) ─── */
	const filterSections = (
		<div className="divide-border/60 divide-y">
			{/* Category */}
			{categoryOptions.length > 0 && onCategoryToggle && (
				<SidebarSection
					title={t("category")}
					sectionKey="category"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{categoryOptions.map((cat) => (
						<SidebarCheckbox
							key={cat.slug}
							label={cat.name}
							count={cat.count}
							checked={selectedCategories.includes(cat.slug)}
							onToggle={() => onCategoryToggle(cat.slug)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Price */}
			{priceRanges.length > 0 && onPriceRangeChange && (
				<SidebarSection
					title={t("price")}
					sectionKey="price"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{priceRanges.map((range) => (
						<SidebarRadio
							key={range.value}
							label={range.label}
							checked={selectedPriceRange === range.value}
							onToggle={() => onPriceRangeChange(selectedPriceRange === range.value ? null : range.value)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Research Level */}
			{researchLevelOptions.length > 0 && onResearchLevelChange && (
				<SidebarSection
					title={t("researchLevel")}
					sectionKey="researchLevel"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{researchLevelOptions.map((level) => (
						<SidebarRadio
							key={level}
							label={translateResearchLevel(level, locale)}
							checked={selectedResearchLevel === level}
							onToggle={() => onResearchLevelChange(selectedResearchLevel === level ? "all" : level)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Availability */}
			{visibleAvailabilityOptions.length > 0 && onAvailabilityChange && (
				<SidebarSection
					title={t("availability")}
					sectionKey="availability"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{visibleAvailabilityOptions.map((a) => (
						<SidebarRadio
							key={a}
							label={translateAvailability(a, locale)}
							checked={selectedAvailability === a}
							onToggle={() => onAvailabilityChange(selectedAvailability === a ? "all" : a)}
						/>
					))}
				</SidebarSection>
			)}

			{/* Peptide Categories (chip-style) */}
			{peptideCategoryOptions.length > 0 && onPeptideCategoryToggle && (
				<SidebarSection
					title={t("peptideCategories")}
					sectionKey="peptideCategory"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					<div className="flex flex-wrap gap-1.5">
						{peptideCategoryOptions.map((opt) => {
							const isSelected = selectedPeptideCategories.includes(opt.name);
							return (
								<button
									key={opt.name}
									type="button"
									onClick={() => onPeptideCategoryToggle(opt.name)}
									className={cn(
										"rounded-full px-2.5 py-1 text-[12px] font-medium transition-all duration-150",
										isSelected
											? "bg-foreground text-background shadow-sm"
											: "bg-muted/80 text-muted-foreground hover:bg-muted hover:text-foreground",
									)}
									aria-pressed={isSelected}
								>
									{translatePeptideCategory(opt.name, locale)}
									<span className={cn("ml-1 tabular-nums", isSelected ? "opacity-50" : "opacity-40")}>
										{opt.count}
									</span>
								</button>
							);
						})}
					</div>
				</SidebarSection>
			)}

			{/* Color */}
			{colorOptions.length > 0 && onColorToggle && (
				<SidebarSection
					title={t("color")}
					sectionKey="color"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					{colorOptions.map((color) => (
						<SidebarCheckbox
							key={color.name}
							label={color.name}
							count={color.count}
							checked={selectedColors.includes(color.name)}
							onToggle={() => onColorToggle(color.name)}
							swatch={color.hex}
						/>
					))}
				</SidebarSection>
			)}

			{/* Size */}
			{sizeOptions.length > 0 && onSizeToggle && (
				<SidebarSection
					title={t("size")}
					sectionKey="size"
					expandedSections={expandedSections}
					onToggle={toggleSection}
				>
					<div className="flex flex-wrap gap-1.5">
						{sizeOptions.map((size) => {
							const isSelected = selectedSizes.includes(size.name);
							return (
								<button
									key={size.name}
									onClick={() => onSizeToggle(size.name)}
									className={cn(
										"rounded-md border px-3 py-1 text-[13px] font-medium transition-all duration-150",
										isSelected
											? "border-foreground bg-foreground text-background"
											: "hover:border-foreground/30 border-border text-muted-foreground hover:text-foreground",
									)}
									aria-pressed={isSelected}
								>
									{size.name}
								</button>
							);
						})}
					</div>
				</SidebarSection>
			)}
		</div>
	);

	return (
		<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
			{/* ─── Top Bar: Search · Count · Sort ─── */}
			<div className="border-border/60 flex items-center gap-2.5 border-b py-3.5">
				{/* Mobile filter trigger */}
				{hasFilters && (
					<button
						onClick={() => setMobileFiltersOpen(true)}
						className="hover:bg-muted/50 inline-flex h-9 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium shadow-xs transition-colors lg:hidden"
					>
						<SlidersHorizontal className="h-4 w-4" />
						<span>{t("filters")}</span>
						{activeFilterCount > 0 && (
							<span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-bold tabular-nums text-background">
								{activeFilterCount}
							</span>
						)}
					</button>
				)}

				{/* Search */}
				{onSearchChange && (
					<DebouncedSearchInput
						searchQuery={searchQuery}
						onSearchChange={onSearchChange}
						placeholder={t("searchPlaceholder")}
					/>
				)}

				<div className="flex-1" />

				{/* Result count — range form when the result spans multiple pages */}
				<span className="hidden text-sm tabular-nums text-muted-foreground sm:block" aria-live="polite">
					<ResultCount text={resultText} />
				</span>

				{/* Sort */}
				<DropdownMenu>
					<DropdownMenuTrigger className="hover:border-foreground/20 data-[state=open]:ring-primary/10 inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-border bg-card px-3.5 text-sm font-medium shadow-xs transition-all hover:shadow-sm data-[state=open]:border-primary data-[state=open]:ring-2">
						{t("sort")}
						<ChevronDown className="h-3.5 w-3.5 opacity-40" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-52">
						<DropdownMenuRadioGroup value={sortValue} onValueChange={(v) => onSortChange(v as SortOption)}>
							<DropdownMenuRadioItem value="featured">{t("sortFeatured")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="newest">{t("sortNewest")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="name_asc">{t("sortNameAsc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="name_desc">{t("sortNameDesc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="price_asc">{t("sortPriceAsc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="price_desc">{t("sortPriceDesc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="score_desc">{t("sortScoreDesc")}</DropdownMenuRadioItem>
							<DropdownMenuRadioItem value="bestselling">{t("sortBestselling")}</DropdownMenuRadioItem>
						</DropdownMenuRadioGroup>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>

			{/* ─── Active Filter Pills ─── */}
			{activeFilters.length > 0 && onRemoveFilter && onClearFilters && (
				<div className="border-border/40 flex flex-wrap items-center gap-1.5 border-b py-2.5">
					{activeFilters.map((filter) => {
						const displayValue =
							filter.key === "researchLevel"
								? translateResearchLevel(filter.value, locale)
								: filter.key === "availability"
									? translateAvailability(filter.value, locale)
									: filter.key === "peptideCategory"
										? translatePeptideCategory(filter.value, locale)
										: filter.key === "price"
											? formatPriceRangeLabel(filter.value, channel, t)
											: filter.value;
						return (
							<span
								key={`${filter.key}-${filter.value}`}
								className="bg-primary/8 inline-flex shrink-0 items-center gap-1 rounded-md py-1 pl-2.5 pr-1 text-[12px] font-medium text-primary"
							>
								{displayValue}
								<button
									onClick={() => onRemoveFilter(filter.key, filter.value)}
									className="hover:bg-primary/15 rounded p-0.5 transition-colors"
								>
									<X className="h-3 w-3" />
									<span className="sr-only">{t("removeFilter", { value: displayValue })}</span>
								</button>
							</span>
						);
					})}
					<button
						onClick={onClearFilters}
						className="shrink-0 rounded px-2 py-0.5 text-[12px] text-muted-foreground transition-colors hover:text-foreground"
					>
						{t("clearAll")}
					</button>
				</div>
			)}

			{/* ─── Content: Sidebar + Grid ─── */}
			<div className="flex gap-8 pt-6 lg:gap-10">
				{/* Desktop Sidebar */}
				{hasFilters && (
					<aside className="hidden w-[230px] shrink-0 lg:block">
						<nav className="sticky top-20 max-h-[calc(100vh-6rem)] overflow-y-auto overscroll-contain pb-12 pr-2 [-ms-overflow-style:none] [scrollbar-width:thin]">
							{filterSections}
						</nav>
					</aside>
				)}

				{/* Mobile Filter Sheet */}
				<Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
					<SheetContent side="left" className="flex w-[320px] flex-col p-0 sm:w-[360px]">
						<SheetHeader className="flex-row items-center justify-between border-b border-border px-5 py-4">
							<SheetTitle className="text-base font-semibold">{t("filters")}</SheetTitle>
							<SheetCloseButton />
						</SheetHeader>

						<div className="flex-1 overflow-y-auto overscroll-contain px-5 py-2">{filterSections}</div>

						<div className="border-t border-border bg-background p-4">
							<button
								onClick={() => setMobileFiltersOpen(false)}
								className="hover:bg-foreground/90 w-full rounded-lg bg-foreground py-3 text-sm font-semibold text-background transition-colors"
							>
								{t("showResults", { count: resultCount })}
							</button>
							{activeFilterCount > 0 && onClearFilters && (
								<button
									onClick={() => {
										onClearFilters();
										setMobileFiltersOpen(false);
									}}
									className="mt-2 w-full py-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
								>
									{t("clearAllFilters", { count: activeFilterCount })}
								</button>
							)}
						</div>
					</SheetContent>
				</Sheet>

				{/* Main Content */}
				<div className="min-w-0 flex-1 pb-8">{children}</div>
			</div>
		</div>
	);
}

function ResultCount({ text }: { text: string }) {
	const resultRef = useRef<HTMLSpanElement>(null);
	const previousText = useRef(text);

	useEffect(() => {
		if (previousText.current === text) return;
		previousText.current = text;

		const result = resultRef.current;
		if (!result) return;
		result.classList.remove("motion-result-count");
		void result.offsetWidth;
		result.classList.add("motion-result-count");
	}, [text]);

	return <span ref={resultRef}>{text}</span>;
}

/* ─────────────────────────────────────────────────
 * Search input — debounced because every committed
 * query triggers a server round trip (URL navigation)
 * ───────────────────────────────────────────────── */

const SEARCH_DEBOUNCE_MS = 350;

function DebouncedSearchInput({
	searchQuery,
	onSearchChange,
	placeholder,
}: {
	searchQuery: string;
	onSearchChange: (query: string) => void;
	placeholder: string;
}) {
	const [value, setValue] = useState(searchQuery);
	// Last value handed to onSearchChange — kept in state so the prop-derived
	// reset below can run during render (React's documented adjust-state-during-
	// render pattern). A prop echoing our own commit must NOT clobber what the
	// user typed ahead in the meantime; only truly external changes reset.
	const [lastCommitted, setLastCommitted] = useState(searchQuery);

	if (searchQuery !== lastCommitted) {
		setLastCommitted(searchQuery);
		setValue(searchQuery);
	}

	// Always commit through the LATEST handler: onSearchChange closes over the
	// current URL state (useSearchParams) — firing a stale capture after other
	// filters changed would rebuild the URL from an outdated snapshot and
	// silently revert those changes.
	const onSearchChangeRef = useRef(onSearchChange);
	useEffect(() => {
		onSearchChangeRef.current = onSearchChange;
	});

	// Effect-driven debounce: any newer keystroke re-arms via cleanup, and an
	// external reset (prop change → adjust block above) cancels the pending
	// commit because value === lastCommitted again.
	useEffect(() => {
		if (value === lastCommitted) return;
		const id = setTimeout(() => {
			setLastCommitted(value);
			onSearchChangeRef.current(value);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [value, lastCommitted]);

	const clearNow = () => {
		setValue("");
		setLastCommitted("");
		onSearchChangeRef.current("");
	};

	return (
		<div className="relative min-w-0 flex-1 sm:max-w-[260px] sm:flex-initial">
			<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
			<input
				type="text"
				placeholder={placeholder}
				value={value}
				onChange={(e) => setValue(e.target.value)}
				className="focus:ring-primary/10 h-9 w-full rounded-lg border border-border bg-card pl-9 pr-8 text-sm shadow-xs transition-all placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2"
			/>
			{value && (
				<button
					onClick={clearNow}
					className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground transition-colors hover:text-foreground"
				>
					<X className="h-3.5 w-3.5" />
				</button>
			)}
		</div>
	);
}

/* ─────────────────────────────────────────────────
 * Sidebar sub-components
 * ───────────────────────────────────────────────── */

function SidebarSection({
	title,
	sectionKey,
	expandedSections,
	onToggle,
	children,
}: {
	title: string;
	sectionKey: string;
	expandedSections: Set<string>;
	onToggle: (key: string) => void;
	children: ReactNode;
}) {
	const isOpen = expandedSections.has(sectionKey);
	const panelId = useId();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	const handleToggle = () => {
		if (isOpen && panelRef.current?.contains(document.activeElement)) {
			triggerRef.current?.focus();
		}
		onToggle(sectionKey);
	};

	return (
		<div className="py-4 first:pt-1">
			<button
				ref={triggerRef}
				onClick={handleToggle}
				aria-expanded={isOpen}
				aria-controls={panelId}
				className="flex w-full items-center justify-between text-[13px] font-semibold uppercase tracking-wide text-foreground"
			>
				{title}
				<span className="t-icon-swap h-3.5 w-3.5" data-state={isOpen ? "b" : "a"} aria-hidden="true">
					<span className="t-icon" data-icon="a">
						<Plus className="h-3.5 w-3.5 text-muted-foreground" />
					</span>
					<span className="t-icon" data-icon="b">
						<Minus className="h-3.5 w-3.5 text-muted-foreground" />
					</span>
				</span>
			</button>
			<div
				ref={panelRef}
				id={panelId}
				className="motion-filter-section-panel"
				data-open={isOpen}
				aria-hidden={!isOpen}
				inert={!isOpen}
			>
				<div className="motion-filter-section-panel-inner">
					<div className="mt-3 space-y-0.5">{children}</div>
				</div>
			</div>
		</div>
	);
}

function SidebarCheckbox({
	label,
	count,
	checked,
	onToggle,
	swatch,
}: {
	label: string;
	count?: number;
	checked: boolean;
	onToggle: () => void;
	swatch?: string;
}) {
	return (
		<button
			onClick={onToggle}
			type="button"
			role="checkbox"
			aria-checked={checked}
			className="hover:bg-muted/60 group flex w-full items-center gap-2.5 rounded-md px-1.5 py-[5px] text-left transition-colors"
		>
			<span
				className={cn(
					"motion-filter-check flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-[3px] border transition-colors duration-150",
					checked
						? "border-foreground bg-foreground text-background"
						: "border-border/80 group-hover:border-foreground/30",
				)}
				aria-hidden="true"
			>
				<Check className="h-3 w-3" strokeWidth={2.5} />
			</span>
			{swatch && (
				<span
					className={cn(
						"h-3.5 w-3.5 shrink-0 rounded-full border transition-shadow",
						checked ? "border-foreground/40 ring-foreground/10 ring-2" : "border-border/60",
					)}
					style={{ backgroundColor: swatch }}
				/>
			)}
			<span className="text-foreground/75 flex-1 text-[13px] transition-colors group-hover:text-foreground">
				{label}
			</span>
			{count != null && <span className="text-muted-foreground/60 text-[11px] tabular-nums">{count}</span>}
		</button>
	);
}

function SidebarRadio({
	label,
	checked,
	onToggle,
}: {
	label: string;
	checked: boolean;
	onToggle: () => void;
}) {
	return (
		<button
			onClick={onToggle}
			type="button"
			aria-pressed={checked}
			className="hover:bg-muted/60 group flex w-full items-center gap-2.5 rounded-md px-1.5 py-[5px] text-left transition-colors"
		>
			<span
				className={cn(
					"flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full border transition-colors duration-150",
					checked ? "border-foreground bg-foreground" : "border-border/80 group-hover:border-foreground/30",
				)}
			>
				{checked && <span className="h-1.5 w-1.5 rounded-full bg-background" />}
			</span>
			<span className="text-foreground/75 flex-1 text-[13px] transition-colors group-hover:text-foreground">
				{label}
			</span>
		</button>
	);
}
